import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import InputBox from "../../components/ui/InputBox";
import Button from "../../components/ui/Button";
import CheckBox from "../../components/ui/CheckBox";

import "./index.scss";

import LeftSection from "../../components/PreLogin/LoginPage/LeftSection.js";
import RightFooter from "../../components/PreLogin/RightFooter";

import { useSelector, useDispatch } from 'react-redux';
import { loginUser, getLoggedInUserDetails, ssoLoginAuth, ssoCallBackAuth } from '../../store/actions/loginAction.js';

import { msOfficeIcon } from "../../utils/icons.js";

const SSO_FLOW_KEY = 'ms_sso_flow_token';
const SSO_RETURN_KEY = 'ms_sso_return_search';
const DEFAULT_LANDING = "/scan-info/exposure/dashboard?customer=all&organization=all&domain=all&scanId=null";

function safeNext(raw) {
    if (!raw) return '/';
    try {
        const decoded = decodeURIComponent(raw);
        // Must start with a single "/" and not "//" or "/\"
        if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.startsWith('/\\')) {
            return '/';
        }
        return decoded;
    } catch {
        return '/';
    }
}

// The "next" param is lost when Microsoft redirects back, so read it from the
// query string we saved before leaving.
function getSavedNext() {
    const saved = sessionStorage.getItem(SSO_RETURN_KEY) || '';
    return new URLSearchParams(saved).get('next');
}

const LoginPage = () => {

    const response = useSelector((state) => state.login?.loginResponse);
    const userDetailsResponse = useSelector((state) => state?.login?.loggedInUserDetailResponse);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Params Microsoft sends back to the redirect URI
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const msError = searchParams.get('error');
    const msErrorDescription = searchParams.get('error_description');
    const isSsoReturn = Boolean(code || msError);

    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [keepLogin, setKeepLogin] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [ssoLoading, setSsoLoading] = useState(isSsoReturn && !msError);

    // Frozen on first render so cleaning the URL later doesn't change it
    const [next] = useState(() =>
        safeNext(isSsoReturn ? getSavedNext() : searchParams.get('next'))
    );

    const callbackStarted = useRef(false);

    const inputChange = (val, type) => {
        if (type === "text") {
            setUserName(val)
        } else {
            setPassword(val)
        }
    }

    const buttonClicked = () => {
        const formData = new FormData();
        formData.append('username', userName);
        formData.append('password', password);
        setErrorMsg('');
        setIsLoading(true)
        dispatch(loginUser(formData))
    }

    const isDisabledButton = () => {
        return userName === '' || password === '';
    }

    const setCheckboxvalue = (e) => {
        if (e.target.checked) {
            setKeepLogin(!keepLogin)
        }
    }

    const forgetPasswordClicked = () => {
        navigate("/auth/forget-password");
    }

    const reset2Fa = () => {
        navigate("/auth/reset-2fa")
    }

    const handleRegisterClick = () => {
        navigate('/auth/register');
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !isDisabledButton() && !ssoLoading) {
            buttonClicked();
        }
    }

    // Shared by password login and SSO callback: store token, then 2FA or fetch user.
    const handleAuthResponse = useCallback((res) => {
        if (!res?.access_token) return false;
        localStorage.setItem('accessToken', res.access_token);
        if (res.require_2fa) {
            navigate(`/auth/verify-account?next=${encodeURIComponent(next)}`, { replace: true });
        } else {
            dispatch(getLoggedInUserDetails());
        }
        return true;
    }, [dispatch, navigate, next]);

    // Step 1: get the authorize URL from the backend and send the user to Microsoft
    const ssoLogin = async () => {
        setErrorMsg('');
        setSsoLoading(true);
        const data = await dispatch(ssoLoginAuth());
        if (!data?.authorize_url || !data?.flow_token) {
            setSsoLoading(false);
            setErrorMsg('Could not start Microsoft sign-in. Please try again.');
            return;
        }
        sessionStorage.setItem(SSO_FLOW_KEY, data.flow_token);
        sessionStorage.setItem(SSO_RETURN_KEY, window.location.search); // keeps ?next=
        window.location.assign(data.authorize_url);
    };

    // Clear any old session once, when the page opens
    useEffect(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('loggedInUserDetails');
    }, []);

    // Step 2: Microsoft redirected back here with ?code=&state= (or ?error=)
    useEffect(() => {
        if (!isSsoReturn || callbackStarted.current) return; // ref guards StrictMode double-run
        callbackStarted.current = true;

        const flowToken = sessionStorage.getItem(SSO_FLOW_KEY);
        sessionStorage.removeItem(SSO_FLOW_KEY);
        sessionStorage.removeItem(SSO_RETURN_KEY);

        // Remove code/state from the address bar so a refresh can't replay them
        navigate(window.location.pathname, { replace: true });

        if (msError) {
            setErrorMsg(msErrorDescription || 'Microsoft sign-in was cancelled.');
            return;
        }
        if (!flowToken) {
            setSsoLoading(false);
            setErrorMsg('Your sign-in session expired. Please try again.');
            return;
        }

        dispatch(ssoCallBackAuth({ code, state, flow_token: flowToken })).then((res) => {
            if (!handleAuthResponse(res)) {
                setSsoLoading(false);
                setErrorMsg(res?.description || 'Microsoft sign-in failed. Please try again.');
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Username/password login response
    useEffect(() => {
        if (response == null) return;
        if (!handleAuthResponse(response)) {
            setIsLoading(false);
            setErrorMsg(response?.description || 'Login failed.');
        }
    }, [response, handleAuthResponse]);

    // Step 3 (both flows): user details loaded, go to destination
    useEffect(() => {
        if (userDetailsResponse !== null && localStorage.getItem('accessToken')) {
            const obj = {
                ...userDetailsResponse,
                language: "en"
            }
            localStorage.setItem('loggedInUserDetails', JSON.stringify(obj))
            navigate(next === "/" ? DEFAULT_LANDING : next, { replace: true });
        }
    }, [userDetailsResponse, navigate, next])


    return (
        <div className='login-container' onKeyDown={handleKeyDown}>
            <Row className='h-100 m-0'>
                <Col md="6" className="h-100">
                    <LeftSection />
                </Col>
                <Col md="6" className="h-100">
                    <div className='login-right-section px-4 mx-3 h-100'>
                        <div className='right-container'>
                            <h1 className='mb-4'>{t("loginPage.rightSection.login")}</h1>
                            <p className="description">{t("loginPage.rightSection.secondaryText")}</p>
                            <button className="ssoButton" onClick={ssoLogin} disabled={ssoLoading || isLoading}>
                                <span>{msOfficeIcon()}</span>
                                {ssoLoading ? 'Signing in with Microsoft...' : 'Continue with Microsoft'}
                            </button>
                            <div className="borderContainer">
                                <div className="border"></div>
                                <div className="text">or</div>
                                <div className="border"></div>
                            </div>
                            {errorMsg && <p className="error-msg text-center mt-3">{errorMsg}</p>}
                            <div className='input-container mt-3'>
                                <InputBox placeholder={'Username'} inputType={'text'} inputLabel={'Username'} inputChange={inputChange} inputValue={userName} />
                                <InputBox placeholder={'Password'} inputType={'password'} inputLabel={'Password'} inputChange={inputChange} inputValue={password} />
                            </div>
                            <div className='submit-button'>
                                {!isLoading && <Button buttonText={"Login"} buttonType="primary" buttonClicked={buttonClicked} disableButton={isDisabledButton() || ssoLoading} />}
                                {isLoading && <Button buttonText={"Loading..."} buttonType="primary" disableButton={true} />}
                            </div>
                            <div className='d-flex justify-content-between mt-3'>
                                <div className='loggedin-checkbox'>
                                    <CheckBox inputLabel={'Keep me logged in'} index={0} checkBoxId={'0'} handleCheckboxChange={setCheckboxvalue} checkBoxValue={'keepLogin'} disable={false} />
                                </div>
                                <div className='forget-password'>
                                    <Button buttonText={"Forget Password?"} buttonType="link" buttonClicked={forgetPasswordClicked} disableButton={isDisabledButton()} /> / <Button buttonText={"Reset 2FA?"} buttonType="link" buttonClicked={reset2Fa} />
                                </div>
                            </div>
                            <div className='not-register mt-5'>
                                <div>
                                    <span>{t('loginPage.rightSection.notRegistered')} </span>
                                    <Button buttonText={"Create an Account"} buttonType="link" buttonClicked={handleRegisterClick} />
                                </div>
                            </div>
                        </div>
                        <RightFooter />
                    </div>
                </Col>
            </Row>
        </div>
    )
}

export default LoginPage;